import { Grid, Dialog, Typography, CircularProgress, Button } from "@mui/material";
import React, { FC, useCallback, useContext, useEffect, useState } from "react";
import { ModalContext } from "context/ModalContext";
import { LocaleContext } from "context/LocaleContext";
import { DialogHeader, WButton, WIcon, WIconTypes, WTypography } from "components";
import { NotificationContext } from "context/NotificationContext";
import { BreakpointsContext } from "context/BreakpointContext";
import { GTMContext } from "context/GTMContext";
import {
  getSubscriptions,
  createSubscription,
  updateSubscription,
  deleteSubscription,
  Subscription,
} from "adapters/SubscriptionsAdapter";
import { LoaderContext } from "context/LoaderContext";
import { usePagination } from "hooks/usePagination";
import { colors } from "styles/colors";

export const ProfileNotificationsModal: FC = () => {
  const { hideModal } = useContext(ModalContext);
  const { localize } = useContext(LocaleContext);
  const { addMessage } = useContext(NotificationContext);
  const { isDesktop } = useContext(BreakpointsContext);
  const { gtmAddToDataLayer } = useContext(GTMContext);
  const { isLoading } = useContext(LoaderContext);

  const [currentSubscription, setCurrentSubscription] = useState<Subscription | undefined>(
    undefined
  );
  const [subscribeToNewJobs, setSubscribeToNewJobs] = useState(false);
  const [interval, setInterval] = useState<"weekly" | "daily">("weekly");

  const { results } = usePagination<Subscription>(
    getSubscriptions,
    "error.generalError",
    "GET_SUBSCRIPTIONS",
    { page_size: 1 }
  );

  useEffect(() => {
    if (results.length === 0 || currentSubscription) {
      return;
    }

    const subscription = results[0];
    setCurrentSubscription(subscription);
    if (subscription) {
      setSubscribeToNewJobs(true);
      setInterval(subscription.time_interval);
    }
  }, [results]);

  const handleSave = useCallback(async (): Promise<void> => {
    if (!subscribeToNewJobs) {
      if (!currentSubscription) {
        hideModal();
        return;
      }
      await deleteSubscription(currentSubscription.id);
      gtmAddToDataLayer({}, "subscription_paused");
      addMessage({ content: "success.jobSubscription", type: "success" });
      hideModal();
      return;
    }

    if (!currentSubscription) {
      const subscription = {
        department_roles: [],
        regions: [],
        time_interval: interval,
      };
      const created = await createSubscription(subscription);
      if (created.data) {
        setCurrentSubscription(created.data);
        gtmAddToDataLayer({}, "subscription_started");
      }
    } else {
      const updatedProps: Partial<Subscription> = {};
      if (currentSubscription.time_interval !== interval) {
        updatedProps.time_interval = interval;
      }

      // Left until removed from backend
      updatedProps.department_roles = [];
      updatedProps.regions = [];
      await updateSubscription(currentSubscription.id, updatedProps);
    }

    addMessage({ content: "success.jobSubscription", type: "success" });
    hideModal();
  }, [
    subscribeToNewJobs,
    currentSubscription,
    interval,
    gtmAddToDataLayer,
    addMessage,
    hideModal,
  ]);

  let topContent = null;
  let bottomContent = null;

  if (isLoading("GET_SUBSCRIPTIONS")) {
    topContent = <CircularProgress size="60px" sx={{ margin: "100px auto" }} />;
  } else {
    topContent = (
      <>
        <Typography width="100%">{localize("form.jobSubscription")}</Typography>

        <Grid container gap="20px" flexWrap="nowrap">
          <BigButton
            title={localize("common.never")}
            selected={!subscribeToNewJobs}
            onClick={() => setSubscribeToNewJobs(false)}
          />
          <BigButton
            title={localize("common.week")}
            subTitle={localize("form.weekSendDay")}
            selected={subscribeToNewJobs && interval === "weekly"}
            onClick={() => {
              setInterval("weekly");
              setSubscribeToNewJobs(true);
            }}
          />
          <BigButton
            title={localize("common.day")}
            selected={subscribeToNewJobs && interval === "daily"}
            onClick={() => {
              setInterval("daily");
              setSubscribeToNewJobs(true);
            }}
          />
        </Grid>
      </>
    );
  }

  if (subscribeToNewJobs) {
    bottomContent = (
      <>
        <Typography width="100%" marginTop="20px">
          {localize("form.limitEmails")}
        </Typography>
      </>
    );
  } else {
    bottomContent = (
      <WTypography variant="body2" marginTop="20px">
        {localize("form.moreFilterOptions")}
      </WTypography>
    );
  }

  return (
    <Dialog
      fullScreen={isDesktop ? false : true}
      open={true}
      sx={{ height: "100%" }}
      onClose={hideModal}
      PaperProps={
        isDesktop
          ? {
              style: { borderRadius: "15px", width: "600px" },
            }
          : undefined
      }
    >
      {!isDesktop && (
        <DialogHeader
          handleClose={() => hideModal()}
          title={localize("profileSections.subscriptions")}
          returnText={localize("profileSections.appSettings")}
        />
      )}
      <Grid container direction="column" padding="1rem" gap=".5rem">
        <Grid item display="flex" justifyContent="center">
          <img
            alt="job image"
            src={`${process.env.PUBLIC_URL}/assets/images/newsletter.svg`}
            style={{
              width: "300px",
              objectFit: "cover",
              marginBottom: "30px",
            }}
          />
        </Grid>
        {isDesktop && (
          <Typography variant="h6">{localize("profileSections.subscriptions")}</Typography>
        )}

        {topContent}

        {bottomContent}

        {!isLoading("GET_SUBSCRIPTIONS") ? (
          <WButton
            fullWidth
            variant="outlined"
            sx={{ marginTop: "30px" }}
            startIcon={<WIcon icon={WIconTypes.check} />}
            onClick={handleSave}
          >
            {localize("form.save")} {localize("profileSections.appSettings").toLowerCase()}
          </WButton>
        ) : null}
      </Grid>
    </Dialog>
  );
};

interface BigButtonProps {
  title: string;
  subTitle?: string;
  selected: boolean;
  onClick: () => void;
}

const BigButton: FC<BigButtonProps> = ({ title, subTitle, selected, onClick }) => {
  return (
    <Grid item width="100%">
      <Button
        variant="outlined"
        onClick={onClick}
        sx={{
          width: "100%",
          height: "70px",
          border: "none !important",
          display: "flex",
          flexDirection: "column",
          boxShadow: selected
            ? `0 0 0 4px ${colors.primaryGreen}`
            : `0 0 0 2px ${colors.lightBorder}`,
        }}
      >
        <WTypography fontSize="1.2rem" fontWeight="bold">
          {title}
        </WTypography>
        <WTypography variant="body2" fontSize="0.9rem">
          {subTitle}
        </WTypography>
      </Button>
    </Grid>
  );
};
